// Redux sagas for User KPI Management

import apiRequest from '@libs/apiRequest';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { setSnack } from 'utilities/redux.actions';
import { API_ENDPOINTS, getErrorMessage } from './common';
import * as actions from './redux.actions';
import * as selectors from './redux.selectors';

// Fetch all user KPIs with pagination and filters
function* fetchUserKpisSaga() {
    try {
        const filters = yield select(selectors.selectFilters);
        const pagination = yield select(selectors.selectPagination);

        // Map column IDs to API sort field names
        const sortFieldMapping = {
            'score': 'score'
        };
        const apiSortBy = sortFieldMapping[filters.sortBy] || filters.sortBy;

        const params = {
            limit: pagination.limit,
            offset: pagination.offset,  // API expects 0-based offset
            ...(filters.search && { search: filters.search }),
            ...(filters.userId && { 'user-id': filters.userId }),
            ...(filters.periodId && { 'period-id': filters.periodId }),
            ...(filters.status && { status: filters.status }),
            ...(filters.departmentId && { department: filters.departmentId }),
            ...(filters.reportingManager && { 'reporting-manager': filters.reportingManager }),
            ...(filters.directLineManager && { 'direct-line-manager': filters.directLineManager }),
            'sort-by': apiSortBy,
            'sort-descending': filters.sortDescending
        };

        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_ALL,
            method: 'GET',
            params
        });

        // API returns { data: { data: [...], limit, offset, total } }
        const responseData = response.data || response;
        yield put(actions.fetchUserKpisSuccess(
            responseData.data || [],
            {
                total: responseData.total || 0,
                totalPages: Math.ceil((responseData.total || 0) / pagination.limit),
                limit: pagination.limit,
                offset: pagination.offset
            }
        ));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch user KPIs');
        yield put(actions.fetchUserKpisFailure(errorMessage));
        yield put(setSnack({
            message: errorMessage,
            severity: 'error',
            open: true
        }));
    }
}

// Fetch user KPI by ID
function* fetchUserKpiByIdSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_BY_ID}/${action.payload}`,
            method: 'GET'
        });

        const responseData = response.data || response;
        yield put(actions.fetchUserKpiByIdSuccess(responseData));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch user KPI details');
        yield put(actions.fetchUserKpiByIdFailure(errorMessage));
        yield put(setSnack({
            message: errorMessage,
            severity: 'error',
            open: true
        }));
    }
}

// Fetch user KPI with goals
function* fetchUserKpiWithGoalsSaga(action) {
    try {
        const { userId, periodId } = action.payload;
        const url = API_ENDPOINTS.GET_WITH_GOALS
            .replace(':userId', userId)
            .replace(':periodId', periodId);
        
        const response = yield call(apiRequest, {
            url,
            method: 'GET'
        });

        if (response) {
            // Handle potential data wrapper from API
            const userKpiData = response.data || response;
            
            // Fetch scores for this user KPI
            let scoresData = [];
            if (userKpiData.id) {
                try {
                    const scoresResponse = yield call(apiRequest, {
                        url: `${API_ENDPOINTS.GET_SCORES_BY_USER_KPI}/${userKpiData.id}`,
                        method: 'GET'
                    });
                    scoresData = scoresResponse.data || [];
                } catch (scoresError) {
                    // If scores fetch fails, continue without scores
                    console.warn('Failed to fetch scores:', scoresError);
                }
            }
            
            // Group scores by userKpiGoalId
            const scoresByGoalId = {};
            scoresData.forEach(score => {
                const goalId = score.userKpiGoalId;
                if (!scoresByGoalId[goalId]) {
                    scoresByGoalId[goalId] = [];
                }
                scoresByGoalId[goalId].push(score);
            });
            
            // Merge scores into goals
            const goalsWithScores = (userKpiData.goals || []).map(goal => ({
                ...goal,
                scores: scoresByGoalId[goal.id] || []
            }));
            
            // Update the user KPI data with goals that include scores
            const userKpiWithScores = {
                ...userKpiData,
                goals: goalsWithScores
            };
            
            yield put(actions.fetchUserKpiWithGoalsSuccess(userKpiWithScores));
        } else {
            throw new Error('Failed to fetch user KPI with goals');
        }
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch user KPI details');
        yield put(actions.fetchUserKpiWithGoalsFailure(errorMessage));
        yield put(setSnack({
            message: errorMessage,
            severity: 'error',
            open: true
        }));
    }
}

// Assign KPI to user
function* assignUserKpiSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.ASSIGN,
            method: 'POST',
            data: JSON.stringify(action.payload)
        });

        if (response) {
            yield put(actions.assignUserKpiSuccess(response));
            yield put(setSnack({
                message: 'KPI assigned to user successfully',
                severity: 'success',
                open: true
            }));
            yield put(actions.fetchUserKpisRequest());
        } else {
            throw new Error('Failed to assign KPI to user');
        }
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to assign KPI to user');
        yield put(actions.assignUserKpiFailure(errorMessage));
        yield put(setSnack({
            message: errorMessage,
            severity: 'error',
            open: true
        }));
    }
}

// Delete user KPI
function* deleteUserKpiSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.DELETE}/${action.payload}`,
            method: 'DELETE'
        });

        if (response) {
            yield put(actions.deleteUserKpiSuccess(action.payload));
            yield put(setSnack({
                message: 'User KPI deleted successfully',
                severity: 'success',
                open: true
            }));
            yield put(actions.fetchUserKpisRequest());
        } else {
            throw new Error('Failed to delete user KPI');
        }
    } catch (error) {
        yield put(actions.deleteUserKpiFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to delete user KPI',
            severity: 'error',
            open: true
        }));
    }
}

// Update user KPI
function* updateUserKpiSaga(action) {
    try {
        const { id, data } = action.payload;
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE_USER_KPI}/${id}`,
            method: 'PUT',
            data: JSON.stringify(data)
        });

        if (response) {
            yield put(actions.updateUserKpiSuccess(response));
            yield put(setSnack({
                message: 'User KPI updated successfully',
                severity: 'success',
                open: true
            }));
            yield put(actions.fetchUserKpisRequest());
        } else {
            throw new Error('Failed to update user KPI');
        }
    } catch (error) {
        yield put(actions.updateUserKpiFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to update user KPI',
            severity: 'error',
            open: true
        }));
    }
}

// Add goal to user KPI
function* addGoalToUserKpiSaga(action) {
    try {
        const { userKpiId, goalData } = action.payload;
        const url = API_ENDPOINTS.ADD_GOAL.replace(':id', userKpiId);
        
        const response = yield call(apiRequest, {
            url,
            method: 'POST',
            data: JSON.stringify(goalData)
        });

        if (response) {
            // Extract the actual data from response
            const responseData = response.data || response;
            
            // Enrich the response with the goal details from allGoals
            const allGoals = yield select(state => state.userKpi.allGoals);
            const goalDetails = allGoals.find(g => g.id === responseData.goalId);
            
            const enrichedResponse = {
                ...responseData,
                goal: goalDetails || null
            };
            
            yield put(actions.addGoalToUserKpiSuccess(enrichedResponse));
            yield put(setSnack({
                message: 'Goal added successfully',
                severity: 'success',
                open: true
            }));
            // No need to refetch - reducer updates the state directly
        } else {
            throw new Error('Failed to add goal');
        }
    } catch (error) {
        yield put(actions.addGoalToUserKpiFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to add goal',
            severity: 'error',
            open: true
        }));
    }
}

// Update user KPI goal
function* updateUserKpiGoalSaga(action) {
    try {
        const { userKpiId, goalId, data } = action.payload;
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE_GOAL.replace(':userKpiId', userKpiId).replace(':goalId', goalId)}`,
            method: 'PUT',
            data: JSON.stringify(data)
        });

        if (response) {
            yield put(actions.updateUserKpiGoalSuccess(response));
            yield put(setSnack({
                message: 'Goal updated successfully',
                severity: 'success',
                open: true
            }));
            // Refresh user KPI data
            const currentData = yield select(state => state.userKpi.userKpiWithGoals);
            if (currentData) {
                yield put(actions.fetchUserKpiWithGoalsRequest(currentData.userId, currentData.periodId));
            }
        } else {
            throw new Error('Failed to update goal');
        }
    } catch (error) {
        yield put(actions.updateUserKpiGoalFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to update goal',
            severity: 'error',
            open: true
        }));
    }
}

// Update multiple goals
function* updateMultipleGoalsSaga(action) {
    try {
        const { userKpiId, goals } = action.payload;
        
        // Update goals one by one since there's no bulk update endpoint
        const updatePromises = goals.map(goal => 
            call(apiRequest, {
                url: `/kpi/user-kpi/${userKpiId}/goals/update/${goal.goalId}`,
                method: 'PUT',
                data: JSON.stringify({
                    weight: goal.weight,
                    category: goal.category,
                    score: goal.score,
                    remark: goal.remark
                })
            })
        );

        yield all(updatePromises);

        yield put(actions.updateMultipleGoalsSuccess());
        yield put(setSnack({
            message: `${goals.length} goals updated successfully`,
            severity: 'success',
            open: true
        }));
        // Refresh the goals list
        yield put(actions.fetchUserKpiGoalsRequest(userKpiId));
    } catch (error) {
        yield put(actions.updateMultipleGoalsFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to update goals',
            severity: 'error',
            open: true
        }));
    }
}

// Remove goal from user KPI
function* removeGoalFromUserKpiSaga(action) {
    try {
        const { userKpiId, goalId } = action.payload;
        const response = yield call(apiRequest, {
            url: `/kpi/user-kpi/${userKpiId}/goals/remove/${goalId}`,
            method: 'DELETE'
        });

        if (response) {
            yield put(actions.removeGoalFromUserKpiSuccess(goalId));
            yield put(setSnack({
                message: 'Goal removed successfully',
                severity: 'success',
                open: true
            }));
            // Refresh user KPI data
            const currentData = yield select(state => state.userKpi.userKpiWithGoals);
            if (currentData) {
                yield put(actions.fetchUserKpiWithGoalsRequest(currentData.userId, currentData.periodId));
            }
        } else {
            throw new Error('Failed to remove goal');
        }
    } catch (error) {
        yield put(actions.removeGoalFromUserKpiFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to remove goal',
            severity: 'error',
            open: true
        }));
    }
}

// Fetch user KPI goals
function* fetchUserKpiGoalsSaga(action) {
    try {
        const url = API_ENDPOINTS.GET_GOALS.replace(':id', action.payload);
        const response = yield call(apiRequest, {
            url,
            method: 'GET'
        });

        if (response && Array.isArray(response)) {
            yield put(actions.fetchUserKpiGoalsSuccess(response));
        } else {
            throw new Error('Failed to fetch goals');
        }
    } catch (error) {
        yield put(actions.fetchUserKpiGoalsFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to fetch goals',
            severity: 'error',
            open: true
        }));
    }
}

// Fetch all users for dropdown
function* fetchAllUsersSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/employee/get/all/preview-role-based',
            method: 'GET',
            params: { limit: 500 }
        });

        const responseData = response.data || response;
        const users = responseData.data || [];
        yield put(actions.fetchAllUsersSuccess(Array.isArray(users) ? users : []));
    } catch (error) {
        yield put(actions.fetchAllUsersFailure(error?.message || 'Failed to fetch users'));
        yield put(actions.fetchAllUsersSuccess([]));
        console.error('Failed to fetch users:', error);
    }
}

// Fetch users for Edit Dialog using old preview endpoint
function* fetchUsersForEditSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/employee/get/all/preview',
            method: 'GET',
            params: { limit: 500 }
        });

        const responseData = response.data || response;
        const users = responseData.data || [];
        yield put(actions.fetchUsersForEditSuccess(Array.isArray(users) ? users : []));
    } catch (error) {
        yield put(actions.fetchUsersForEditFailure(error?.message || 'Failed to fetch users'));
        yield put(actions.fetchUsersForEditSuccess([]));
        console.error('Failed to fetch users for edit:', error);
    }
}

// Fetch all periods for dropdown
function* fetchAllPeriodsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/kpi/period/get/all',
            method: 'GET',
            params: { limit: 200 }
        });

        const responseData = response.data || response;
        const periods = responseData.data || [];
        yield put(actions.fetchAllPeriodsSuccess(Array.isArray(periods) ? periods : []));
    } catch (error) {
        yield put(actions.fetchAllPeriodsFailure(error?.message || 'Failed to fetch periods'));
        yield put(actions.fetchAllPeriodsSuccess([]));
        console.error('Failed to fetch periods:', error);
    }
}

// Fetch all templates for dropdown
function* fetchAllTemplatesSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/kpi/template/get/all',
            method: 'GET',
            params: { limit: 200 }
        });

        const responseData = response.data || response;
        const templates = responseData.data || [];
        yield put(actions.fetchAllTemplatesSuccess(Array.isArray(templates) ? templates : []));
    } catch (error) {
        yield put(actions.fetchAllTemplatesFailure(error?.message || 'Failed to fetch templates'));
        yield put(actions.fetchAllTemplatesSuccess([]));
        console.error('Failed to fetch templates:', error);
    }
}

// Fetch all goals for dropdown
function* fetchAllGoalsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/kpi/goal/get/all',
            method: 'GET',
            params: { limit: 500 }
        });

        const responseData = response.data || response;
        const goals = responseData.data || [];
        yield put(actions.fetchAllGoalsSuccess(Array.isArray(goals) ? goals : []));
    } catch (error) {
        yield put(actions.fetchAllGoalsFailure(error?.message || 'Failed to fetch goals'));
        yield put(actions.fetchAllGoalsSuccess([]));
        console.error('Failed to fetch goals:', error);
    }
}

// Fetch all departments for dropdown
function* fetchAllDepartmentsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/department/get/all',
            method: 'GET'
        });

        const responseData = response.data || response;
        const departments = responseData.data || [];
        yield put(actions.fetchAllDepartmentsSuccess(Array.isArray(departments) ? departments : []));
    } catch (error) {
        yield put(actions.fetchAllDepartmentsFailure(error?.message || 'Failed to fetch departments'));
        yield put(actions.fetchAllDepartmentsSuccess([]));
        console.error('Failed to fetch departments:', error);
    }
}

function* fetchAllDesignationsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/designation/all',
            method: 'GET'
        });

        const responseData = response.data || response;
        const designations = responseData.data || [];
        yield put(actions.fetchAllDesignationsSuccess(Array.isArray(designations) ? designations : []));
    } catch (error) {
        yield put(actions.fetchAllDesignationsFailure(error?.message || 'Failed to fetch designations'));
        yield put(actions.fetchAllDesignationsSuccess([]));
        console.error('Failed to fetch designations:', error);
    }
}

// Fetch all reporting managers for dropdown
function* fetchAllReportingManagersSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/employee/get/reporting-managers',
            method: 'GET'
        });

        const responseData = response.data || response;
        const managers = Array.isArray(responseData) ? responseData : (responseData.data || []);
        yield put(actions.fetchAllReportingManagersSuccess(Array.isArray(managers) ? managers : []));
    } catch (error) {
        yield put(actions.fetchAllReportingManagersFailure(error?.message || 'Failed to fetch reporting managers'));
        yield put(actions.fetchAllReportingManagersSuccess([]));
        console.error('Failed to fetch reporting managers:', error);
    }
}

// Fetch direct line managers for dropdown
function* fetchDirectLineManagersSaga(action) {
    try {
        const managerEmail = action.payload;
        const response = yield call(apiRequest, {
            url: '/employee/get/direct-line-managers',
            method: 'GET',
            params: managerEmail ? { 'manager-email': managerEmail } : {}
        });

        const responseData = response.data || response;
        const managers = Array.isArray(responseData) ? responseData : (responseData.data || []);
        yield put(actions.fetchDirectLineManagersSuccess(Array.isArray(managers) ? managers : []));
    } catch (error) {
        yield put(actions.fetchDirectLineManagersFailure(error?.message || 'Failed to fetch direct line managers'));
        yield put(actions.fetchDirectLineManagersSuccess([]));
        // Fail silently without breaking UI if endpoint not yet deployed
    }
}

// Fetch scores for a User KPI
function* fetchUserKpiScoresSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_SCORES_BY_USER_KPI}/${action.payload}`,
            method: 'GET'
        });

        const responseData = response.data || response;
        const scores = Array.isArray(responseData) ? responseData : [];
        yield put(actions.fetchUserKpiScoresSuccess(scores));
    } catch (error) {
        yield put(actions.fetchUserKpiScoresFailure(error?.message || 'Failed to fetch scores'));
        console.error('Failed to fetch scores:', error);
    }
}

// Create bulk scores
function* createBulkScoresSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE_BULK_SCORES,
            method: 'POST',
            data: JSON.stringify({ scores: action.payload })
        });

        if (response) {
            const responseData = Array.isArray(response) ? response : response.data || [];
            yield put(actions.createBulkScoresSuccess(responseData));
            
            yield put(setSnack({
                message: 'Scores saved successfully',
                severity: 'success',
                open: true
            }));
        } else {
            throw new Error('Failed to save scores');
        }
    } catch (error) {
        console.error('Error creating bulk scores:', error);
        yield put(actions.createBulkScoresFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to save scores',
            severity: 'error',
            open: true
        }));
    }
}

// Create single score
function* createScoreSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE_SCORE,
            method: 'POST',
            data: JSON.stringify(action.payload)
        });

        if (response) {
            yield put(actions.createScoreSuccess(response));
            
            yield put(setSnack({
                message: 'Score added successfully',
                severity: 'success',
                open: true
            }));
        } else {
            throw new Error('Failed to add score');
        }
    } catch (error) {
        console.error('❌ [SAGA] CREATE_SCORE - Error:', error);
        yield put(actions.createScoreFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to add score',
            severity: 'error',
            open: true
        }));
    }
}

// Update score
function* updateScoreSaga(action) {
    try {
        const { id, scoreData } = action.payload;
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE_SCORE}/${id}`,
            method: 'PUT',
            data: JSON.stringify(scoreData)
        });

        if (response) {
            yield put(actions.updateScoreSuccess(response));
            
            yield put(setSnack({
                message: 'Score updated successfully',
                severity: 'success',
                open: true
            }));
        } else {
            throw new Error('Failed to update score');
        }
    } catch (error) {
        console.error('❌ [SAGA] UPDATE_SCORE - Error:', error);
        yield put(actions.updateScoreFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to update score',
            severity: 'error',
            open: true
        }));
    }
}

// Delete score
function* deleteScoreSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.DELETE_SCORE}/${action.payload}`,
            method: 'DELETE'
        });

        if (response) {
            // Optimistically update Redux state by removing deleted score - NO API REFETCH
            yield put(actions.deleteScoreSuccess(action.payload));
            
            yield put(setSnack({
                message: 'Score deleted successfully',
                severity: 'success',
                open: true
            }));
        } else {
            throw new Error('Failed to delete score');
        }
    } catch (error) {
        yield put(actions.deleteScoreFailure(error.message));
        yield put(setSnack({
            message: error.message || 'Failed to delete score',
            severity: 'error',
            open: true
        }));
    }
}

// Root saga
export default function* userKpiSaga() {
    yield takeLatest(actions.FETCH_USER_KPIS_REQUEST, fetchUserKpisSaga);
    yield takeLatest(actions.FETCH_USER_KPI_BY_ID_REQUEST, fetchUserKpiByIdSaga);
    yield takeLatest(actions.FETCH_USER_KPI_WITH_GOALS_REQUEST, fetchUserKpiWithGoalsSaga);
    yield takeLatest(actions.ASSIGN_USER_KPI_REQUEST, assignUserKpiSaga);
    yield takeLatest(actions.DELETE_USER_KPI_REQUEST, deleteUserKpiSaga);
    yield takeLatest(actions.UPDATE_USER_KPI_REQUEST, updateUserKpiSaga);
    yield takeLatest(actions.ADD_GOAL_TO_USER_KPI_REQUEST, addGoalToUserKpiSaga);
    yield takeLatest(actions.UPDATE_USER_KPI_GOAL_REQUEST, updateUserKpiGoalSaga);
    yield takeLatest(actions.UPDATE_MULTIPLE_GOALS_REQUEST, updateMultipleGoalsSaga);
    yield takeLatest(actions.REMOVE_GOAL_FROM_USER_KPI_REQUEST, removeGoalFromUserKpiSaga);
    yield takeLatest(actions.FETCH_USER_KPI_GOALS_REQUEST, fetchUserKpiGoalsSaga);
    yield takeLatest(actions.FETCH_ALL_USERS_REQUEST, fetchAllUsersSaga);
    yield takeLatest(actions.FETCH_USERS_FOR_EDIT_REQUEST, fetchUsersForEditSaga);
    yield takeLatest(actions.FETCH_ALL_PERIODS_REQUEST, fetchAllPeriodsSaga);
    yield takeLatest(actions.FETCH_ALL_TEMPLATES_REQUEST, fetchAllTemplatesSaga);
    yield takeLatest(actions.FETCH_ALL_GOALS_REQUEST, fetchAllGoalsSaga);
    yield takeLatest(actions.FETCH_ALL_DEPARTMENTS_REQUEST, fetchAllDepartmentsSaga);
    yield takeLatest(actions.FETCH_ALL_DESIGNATIONS_REQUEST, fetchAllDesignationsSaga);
    yield takeLatest(actions.FETCH_ALL_REPORTING_MANAGERS_REQUEST, fetchAllReportingManagersSaga);
    yield takeLatest(actions.FETCH_DIRECT_LINE_MANAGERS_REQUEST, fetchDirectLineManagersSaga);
    yield takeLatest(actions.FETCH_USER_KPI_SCORES_REQUEST, fetchUserKpiScoresSaga);
    yield takeLatest(actions.CREATE_BULK_SCORES_REQUEST, createBulkScoresSaga);
    yield takeLatest(actions.CREATE_SCORE_REQUEST, createScoreSaga);
    yield takeLatest(actions.UPDATE_SCORE_REQUEST, updateScoreSaga);
    yield takeLatest(actions.DELETE_SCORE_REQUEST, deleteScoreSaga);
}
